import React, { ReactNode, useEffect, useState } from 'react';

import { Loader, Menu } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

import { useLazyGetTransmissionReportIdsQuery } from '@/store/slices/api/patient/patientListApiSlice';
import DownloadIcon from '@/assets/icons/DownloadIcon';
import EditMenuIcon from '@/assets/icons/EditMenuIcon';
import FileIconDark from '@/assets/icons/FileIconDark';
import SignPenIconDark from '@/assets/icons/SignPenIconDark';

import { PatientData } from '@/types/patientList';

import ReportCreateModal from '../../TransmissionReportPage/CreateReport';
import styles from '../cells/cells.module.scss';
import { usePatientActions } from '../hooks/usePatientActions';

import { BatchDownloadModal } from './BatchDownloadModal';

interface PatientOptionsMenuProps {
  patient: PatientData;
  opened: boolean;
  onOpenChange: (opened: boolean) => void;
  children: ReactNode;
}

export const PatientOptionsMenu: React.FC<PatientOptionsMenuProps> = ({
  patient,
  opened,
  onOpenChange,
  children
}) => {
  const [
    getTransmissionReportIds,
    { data: transmissionReportIdsData, isLoading: isLoadingReportIds }
  ] = useLazyGetTransmissionReportIdsQuery();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const openCreateReportModal = () => setIsCreateModalOpen(true);
  const closeCreateReportModal = () => setIsCreateModalOpen(false);
  const {
    isDownloadingPrevious,
    isDownloadingNotDownloaded,
    isDownloadingAll,
    batchDownloadState,
    allReportsBatchState,
    setBatchDownloadState,
    setAllReportsBatchState,
    hasCurrentReport,
    hasPreviousReport,
    notDownloadedCount,
    onViewReport,
    onEditReport,
    onViewPreviousReport,
    onDownloadPreviousReport,
    onDownloadNotDownloadedReports,
    onDownloadAllReports,
    handleBatchDownload,
    handleAllReportsBatchDownload,
    handleClose,
    handleAllReportsClose
  } = usePatientActions(patient, () => onOpenChange(false));

  // Trigger API call when menu opens
  useEffect(() => {
    if (opened && !transmissionReportIdsData) {
      getTransmissionReportIds({ patientIds: [patient.id.toString()] });
    }
  }, [opened, patient.id, getTransmissionReportIds, transmissionReportIdsData]);

  // Extract transmission report IDs for this specific patient from the response
  const allReportIds =
    transmissionReportIdsData?.[patient.id.toString()]?.map((id: any) => id.toString()) || [];
  const allReportsCount = allReportIds.length;

  const menuItems = [
    {
      icon: <SignPenIconDark />,
      label: 'View & Sign Report',
      onClick: onViewReport,
      disabled: !hasCurrentReport,
      isDownload: false
    },
    {
      icon: <EditMenuIcon />,
      label: 'Edit Report',
      onClick: onEditReport,
      disabled: !hasCurrentReport,
      isDownload: false
    },
    {
      icon: <FileIconDark />,
      label: 'View Previous Report',
      onClick: onViewPreviousReport,
      disabled: !hasPreviousReport,
      isDownload: false
    },
    {
      icon: <DownloadIcon />,
      label: 'Previous Report',
      onClick: onDownloadPreviousReport,
      disabled: !hasPreviousReport || isDownloadingPrevious,
      isDownload: true,
      isLoading: isDownloadingPrevious
    },
    {
      icon: <DownloadIcon />,
      label: `Not Downloaded Reports (${notDownloadedCount})`,
      onClick: onDownloadNotDownloadedReports,
      disabled: notDownloadedCount === 0 || isDownloadingNotDownloaded,
      isDownload: true,
      isLoading: isDownloadingNotDownloaded
    },
    {
      icon: <DownloadIcon />,
      label: isLoadingReportIds ? 'All Reports (...)' : `All Reports (${allReportsCount})`,
      onClick: () => onDownloadAllReports(allReportIds),
      disabled: isLoadingReportIds || allReportsCount === 0 || isDownloadingAll,
      isDownload: true,
      isLoading: isDownloadingAll
    }
  ];

  return (
    <>
      <Menu
        shadow="md"
        width={270}
        position="bottom-end"
        offset={5}
        opened={opened}
        onChange={onOpenChange}
        closeOnItemClick={false}>
        <Menu.Target>{children}</Menu.Target>

        <Menu.Dropdown className={styles.menuDropdown}>
          <Menu.Item
            leftSection={<IconPlus />}
            onClick={openCreateReportModal}
            className={styles.menuItem}>
            Create New Report
          </Menu.Item>
          {menuItems.map((item, index) => (
            <Menu.Item
              key={index}
              leftSection={item.isDownload && item.isLoading ? <Loader size={16} /> : item.icon}
              onClick={item.onClick}
              className={styles.menuItem}
              disabled={item.disabled}>
              {item.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      {/* Modal for Not Downloaded Reports */}
      <BatchDownloadModal
        batchDownloadState={batchDownloadState}
        setBatchDownloadState={setBatchDownloadState}
        onDownload={handleBatchDownload}
        onClose={handleClose}
        notDownloadedCount={notDownloadedCount}
      />

      {/* Modal for All Reports */}
      <BatchDownloadModal
        batchDownloadState={allReportsBatchState}
        setBatchDownloadState={setAllReportsBatchState}
        onDownload={handleAllReportsBatchDownload}
        onClose={handleAllReportsClose}
        notDownloadedCount={allReportsCount}
        title="All Reports Download"
        downloadingMessage={`Preparing ${allReportsCount} reports for download...`}
        successMessage={`${allReportsCount} reports are ready for download!`}
      />

      {isCreateModalOpen && (
        <ReportCreateModal
          isOpen={isCreateModalOpen}
          onClose={closeCreateReportModal}
          patientId={patient.id}
        />
      )}
    </>
  );
};
